/**
 * CSV → SQL Tool
 *
 * Convert CSV data to SQL INSERT statements.
 */

import * as Papa from 'papaparse';
import { format as formatSql } from 'sql-formatter';

import { SEMANTIC_TYPES } from '@/detectors/semanticTypes';
import { ToolError } from '@/engine/errors/ToolError';
import type { ITransformerTool } from '@/interfaces/ITool';

type QuoteStyle = 'double' | 'backtick' | 'bracket';

export const CsvToSqlTool: ITransformerTool = {
  id: 'csv-to-sql',
  name: 'CSV → SQL',
  description: 'Convert CSV data to SQL INSERT statements',
  version: '1.0.0',
  type: 'transformer',
  category: 'Converters',
  keywords: ['csv', 'sql', 'insert', 'convert', 'database', 'table'],

  seo: {
    slug: 'csv-to-sql-converter',
    pageTitle: 'CSV to SQL Converter - Free Online Tool | SWE DevTools',
    metaDescription: 'Convert CSV data to SQL INSERT statements instantly. Configurable table name, quote style, and batch size. Fast, private, runs in your browser.',
    keywords: ['csv to sql', 'csv to insert', 'csv sql converter', 'csv to database', 'generate sql from csv'],
    relatedTools: ['csv-to-json', 'sql-format', 'csv-to-xml'],
    schemaType: 'WebApplication',
  },

  supportedInputTypes: [SEMANTIC_TYPES.CSV],
  outputType: SEMANTIC_TYPES.TEXT,

  attributes: [
    {
      name: 'tableName',
      type: 'string',
      label: 'Table Name',
      defaultValue: 'my_table',
      description: 'Name of the SQL table',
    },
    {
      name: 'quoteStyle',
      type: 'select',
      label: 'Identifier Quote',
      defaultValue: 'double',
      options: ['double', 'backtick', 'bracket'],
      description: 'Quote style for identifiers (MySQL=backtick, Standard SQL/PostgreSQL=double, SQL Server=bracket)',
    },
    {
      name: 'batchSize',
      type: 'number',
      label: 'Batch Size',
      defaultValue: 0,
      validation: { min: 0, max: 10000, step: 1 },
      description: 'Rows per INSERT (0 = one INSERT per row)',
    },
    {
      name: 'formatOutput',
      type: 'boolean',
      label: 'Format SQL',
      defaultValue: true,
      description: 'Pretty-print the SQL output',
    },
  ],

  supportsInput(_input: string, detectedType?: string): boolean {
    return detectedType === 'CSV';
  },

  async transform(input: string, config: Record<string, unknown>): Promise<string> {
    const tableName = String(config.tableName ?? 'my_table').trim() || 'my_table';
    const quoteStyle = (config.quoteStyle as QuoteStyle) || 'double';
    const batchSize = Number(config.batchSize ?? 0);
    const shouldFormat = config.formatOutput !== false;

    try {
      const result = Papa.parse<Record<string, string>>(input, {
        header: true,
        skipEmptyLines: true,
        transformHeader: h => h.trim(),
        transform: v => v.trim(),
      });

      if (result.errors.length > 0) {
        throw ToolError.validation(result.errors[0]?.message ?? 'Invalid CSV');
      }

      const columns = result.meta.fields ?? [];
      if (columns.length === 0) return '-- No columns found in CSV';

      const data = result.data.filter(row =>
        columns.some(col => String((row as Record<string, string>)[col] ?? '') !== '')
      );
      if (data.length === 0) return '-- No data rows found in CSV';

      const quoteId = (name: string) => {
        if (quoteStyle === 'backtick') return `\`${name.replace(/`/g, '``')}\``;
        if (quoteStyle === 'bracket') return `[${name.replace(/\]/g, ']]')}]`;
        return `"${name.replace(/"/g, '""')}"`;
      };
      const quotedTable = quoteId(tableName);
      const quotedColumns = columns.map(c => quoteId(c)).join(', ');

      const statements: string[] = [];

      if (batchSize > 0) {
        // Batched inserts
        for (let i = 0; i < data.length; i += batchSize) {
          const batch = data.slice(i, i + batchSize);
          const valueGroups = batch.map(row => {
            const vals = columns.map(col => escapeValue(row[col]));
            return `(${vals.join(', ')})`;
          });
          statements.push(
            `INSERT INTO ${quotedTable} (${quotedColumns}) VALUES\n${valueGroups.join(',\n')};`
          );
        }
      } else {
        // One INSERT per row
        for (const row of data) {
          const vals = columns.map(col => escapeValue(row[col]));
          statements.push(
            `INSERT INTO ${quotedTable} (${quotedColumns}) VALUES (${vals.join(', ')});`
          );
        }
      }

      let sql = statements.join('\n\n');

      if (shouldFormat) {
        try {
          sql = formatSql(sql, { language: 'sql' });
        } catch {
          // Fall through with unformatted SQL if formatter fails
        }
      }

      return sql;
    } catch (e) {
      if (e instanceof ToolError) throw e;
      const error = e as Error;
      throw ToolError.validation(`CSV to SQL failed: ${error.message}`, {
        suggestion: 'Ensure the CSV has a header row and consistent columns',
      });
    }
  },
};

function escapeValue(value: string | undefined | null): string {
  if (value === undefined || value === null || value === '') return 'NULL';

  // Check if it's a number (preserve leading zeros as strings)
  const trimmed = value.trim();
  const num = Number(trimmed);
  if (!isNaN(num) && trimmed !== '') {
    const hasLeadingZero = /^0\d/.test(trimmed);
    if (!hasLeadingZero) return trimmed;
  }

  // Check for boolean-like values
  const lower = value.toLowerCase();
  if (lower === 'true' || lower === 'false') return lower.toUpperCase();
  if (lower === 'null') return 'NULL';

  // Escape single quotes and wrap in quotes
  return `'${value.replace(/'/g, "''")}'`;
}
